import { applicationPaths } from '@core/http/applicationPaths';
import { useApi } from '@core/http/useApi';
import { pmZoneSitesReducer } from '@core/store/entity/pmzoneSites/PMZoneSitesReducer';
import { EntityReducers } from '@core/store/EntityReducers';
import { useGetByIds } from '@core/store/hooks/entityHooks';
import { accountsReducer, selectedAccStateSelector } from '@core/store/library-stores';
import { useAppDispatch, useAppSelector } from '@core/store/store';
import { getPathPayload, useEntityThunk } from '@hwm/ui-lib';
import { t } from '@lingui/macro';
import {
  CriticalPointModel,
  PMZoneSiteModel,
  PMZoneUnitsState,
  PMZoneUpdateInformation,
  PegasusFlowStatusesModel,
  PegasusPredefinedSingleTablesModel,
  PegasusPredefinedTablesAPIModel,
  PegasusTimeStatusesModel,
} from '@shared/models';
import { format } from 'date-fns';
import { saveAs } from 'file-saver';
import { useCallback, useEffect } from 'react';
import { ModeSettingsGetModel } from './ControlView/sections/Tabs/ControlTabs.models';
import { pmZoneSitesUiSlice } from './store/pmZoneSitesUiReducer';
import {
  parseFlowPredefinedTableToPut,
  parsePegasusPredefinedTablesFromGet,
  parseTimePredefinedTableToPut,
} from './ControlView/sections/Tabs/ControlTabs.utils';
import { PredefinedTableType } from '@shared/components/PegasusTables/PegasusPredefinedTables.utils';
import { useNotifications } from '@shared/hooks/useNotifications';
import { preferencesSelector } from '@core/store/entity/preferences/PreferencesSelectors';
import { accountsThunks } from '@core/store/library-stores/accounts.thunks';
import { isEmpty } from 'lodash';

export const useSetAccDataSource = () => {
  const dispatch = useAppDispatch();

  const [setAccData] = useEntityThunk(
    accountsThunks.fetchById,
    'error msg',
    useCallback(
      (response) => {
        dispatch(accountsReducer.slice.actions.setSelectedAccount({ account: response }));
      },
      [dispatch]
    )
  );

  return { setAccData };
};

export const usePmZoneSiteListDataSource = () => {
  const dispatch = useAppDispatch();

  const { pmZoneData, selectedAccount } = useAppSelector(selectedAccStateSelector);

  const pmZoneSites = useGetByIds(
    pmZoneSitesReducer.entityAdapter,
    EntityReducers.pmZoneSites,
    (state) => state['pmZoneSites-ui'].loadedIds
  );

  const loading = useAppSelector((state) => state.pmZoneSites.loadingPending);

  const [getPmZoneSites, pmZoneSitesLoading] = useEntityThunk(
    pmZoneSitesReducer.thunks.getPMZoneSites,
    'error msg',
    useCallback(
      (response) => {
        dispatch(
          pmZoneSitesUiSlice.actions.setIds({
            ids: response.map((site) => site.id.toString()),
          })
        );
      },
      [dispatch]
    )
  );

  const newPmZoneSites = useCallback(
    (prvId: number, cpIds: CriticalPointModel[]) => {
      const newSitesList = pmZoneSites?.map((site) => {
        const newSite: PMZoneSiteModel = { ...site };
        if (site.id === prvId) {
          newSite.siteType = 'PRV';
        }
        if (cpIds.some((cp) => cp.cpId === site.id)) {
          newSite.siteType = 'CP';
        }
        return newSite;
      });
      return newSitesList;
    },
    [pmZoneSites]
  );

  return {
    getPmZoneSites,
    pmZoneSites:
      selectedAccount?.isPmZone && Boolean(pmZoneData) && !isEmpty(pmZoneData)
        ? newPmZoneSites(pmZoneData?.prValveId, pmZoneData?.criticalPoints ?? [])
        : ([] as PMZoneSiteModel[]),
    pmZoneSitesLoading: pmZoneSitesLoading || loading > 0,
  };
};

export const usePMZoneUpdateInfoDataSource = (id?: number, isPmZone?: boolean) => {
  const [getPMZoneUpdateInfo, pmZoneUpdateInfo, pmZoneUpdateInfoLoading] = useApi<PMZoneUpdateInformation>(
    applicationPaths.pmZones.getUpdateInformation({ id }),
    'get'
  );

  useEffect(() => {
    if (id && isPmZone) {
      getPMZoneUpdateInfo();
    }
  }, [getPMZoneUpdateInfo, id, isPmZone]);

  return { pmZoneUpdateInfo, pmZoneUpdateInfoLoading };
};

export const useModeSettingsDataSource = () => {
  const { handleError } = useNotifications();

  const [getModeSettingsData, modeSettingsData, modeSettingsLoading, modeSettingsError] = useApi<ModeSettingsGetModel>(
    '',
    'get'
  );

  const handleGetModeSettingsData = useCallback(
    (accId: number, prvId: number, units: PMZoneUnitsState) => {
      const url = applicationPaths.pmZones.getModeSettings({ accId, prvId });

      getModeSettingsData({
        additionalOptions: {
          url,
          params: units,
        },
      }).catch((err) => {
        const error = err?.response?.data?.errorCode;
        error && handleError(error, t`Control Settings`);
      });
    },
    [getModeSettingsData, handleError]
  );

  return {
    getModeSettingsData: handleGetModeSettingsData,
    modeSettingsData,
    modeSettingsLoading,
    modeSettingsError,
  };
};

export const usePredefinedTablesDataSource = () => {
  const { selectedAccount } = useAppSelector(selectedAccStateSelector);
  const { preferences: units } = useAppSelector(preferencesSelector);

  const { handleError } = useNotifications();

  const [getPredefinedTables, predefinedTables, predefinedTablesLoading] = useApi<
    PegasusPredefinedTablesAPIModel,
    null,
    getPathPayload<typeof applicationPaths.sites.predefinedPegasusTables>
  >(
    useCallback((params) => applicationPaths.sites.predefinedPegasusTables(params), []),
    'get'
  );

  const handleGetPredefinedTables = useCallback(
    (siteId: number) => {
      if (!siteId || !selectedAccount?.id) return;

      getPredefinedTables(
        { siteId },
        {
          additionalOptions: {
            params: {
              accountId: selectedAccount?.id,
              ...units,
            },
          },
        }
      );
    },
    [units, selectedAccount?.id, getPredefinedTables]
  );

  const [savePredefinedTimeTable, , savePredefinedTimeTableLoading] = useApi<
    null,
    null,
    getPathPayload<typeof applicationPaths.sites.predefinedPegasusTimeTables>
  >(
    useCallback((params) => applicationPaths.sites.predefinedPegasusTimeTables(params), []),
    'put'
  );

  const [savePredefinedFlowTable, , savePredefinedFlowTableLoading] = useApi<
    null,
    null,
    getPathPayload<typeof applicationPaths.sites.predefinedPegasusFlowTables>
  >(
    useCallback((params) => applicationPaths.sites.predefinedPegasusFlowTables(params), []),
    'put'
  );

  const handlePutPredefinedTimeTable = useCallback(
    (
      siteId: number,
      data:
        | (PegasusPredefinedSingleTablesModel<PegasusTimeStatusesModel> & {
            type: 'Time';
          })
        | (PegasusPredefinedSingleTablesModel<PegasusFlowStatusesModel> & {
            type: 'Flow';
          }),
      cb: () => void
    ) => {
      if (!siteId) return;

      const savePredefinedTable = data.type === 'Time' ? savePredefinedTimeTable : savePredefinedFlowTable;

      const parsedData = {
        ...(data.type === 'Time'
          ? parseTimePredefinedTableToPut(data, units)
          : parseFlowPredefinedTableToPut(data, units)),
      };

      savePredefinedTable({ siteId }, { additionalOptions: { data: parsedData } })
        .then(() => {
          handleGetPredefinedTables(siteId);
          cb();
        })
        .catch((err) => handleError(err, t`Predefined Tables`));
    },
    [units, handleError, savePredefinedTimeTable, savePredefinedFlowTable, handleGetPredefinedTables]
  );

  const [removePredefinedTimeTable, , removePredefinedTimeTableLoading] = useApi<
    null,
    null,
    getPathPayload<typeof applicationPaths.sites.predefinedPegasusTimeTables>
  >(
    useCallback((params) => applicationPaths.sites.predefinedPegasusTimeTables(params), []),
    'delete'
  );

  const [removePredefinedFlowTable, , removePredefinedFlowTableLoading] = useApi<
    null,
    null,
    getPathPayload<typeof applicationPaths.sites.predefinedPegasusFlowTables>
  >(
    useCallback((params) => applicationPaths.sites.predefinedPegasusFlowTables(params), []),
    'delete'
  );

  const handleDeletePredefinedTables = useCallback(
    (siteId: number, id: number, type: PredefinedTableType) => {
      if (!siteId) return;

      const removePredefinedTables = type === 'Time' ? removePredefinedTimeTable : removePredefinedFlowTable;

      removePredefinedTables({ siteId, tableId: id }).then(() => {
        handleGetPredefinedTables(siteId);
      });
    },
    [removePredefinedTimeTable, removePredefinedFlowTable, handleGetPredefinedTables]
  );

  return {
    getPredefinedTables: handleGetPredefinedTables,
    predefinedTables: parsePegasusPredefinedTablesFromGet(predefinedTables ?? null),
    savePredefinedTable: handlePutPredefinedTimeTable,
    removePredefinedTables: handleDeletePredefinedTables,
    loading:
      predefinedTablesLoading ||
      savePredefinedTimeTableLoading ||
      savePredefinedFlowTableLoading ||
      removePredefinedTimeTableLoading ||
      removePredefinedFlowTableLoading,
  } as const;
};

export type PredefinedTablesDataSourceType = ReturnType<typeof usePredefinedTablesDataSource>;

export const useReportsDataSource = () => {
  const [getFleetReport] = useApi<void, null, { id: number }>(
    ({ id }) => applicationPaths.pmZones.fleetReport({ id }),
    'post'
  );
  const [getActuationsCSV] = useApi<Blob, null, getPathPayload<typeof applicationPaths.accounts.getActuationsReport>>(
    ({ accountId }) => applicationPaths.accounts.getActuationsReport({ accountId }),
    'get'
  );
  const [getMaintenanceReport] = useApi<void, null, { id: number }>(
    ({ id }) => applicationPaths.pmZones.maintenanceReport({ id }),
    'post'
  );

  const { handleSuccess, handleError } = useNotifications();

  const handleGetFleetReport = useCallback(
    ({ id, callInDays }: { id: number; callInDays: number }) => {
      getFleetReport(
        { id },
        {
          additionalOptions: {
            params: {
              callInDays,
            },
          },
        }
      )
        .then(() =>
          handleSuccess(
            t`This export has been added to the export queue, you will be notified by email when the export is complete.`,
            t({ message: 'Fleet report', id: '[Report.Fleet]' })
          )
        )
        .catch(() =>
          handleError(
            t`There was a problem while creating Fleet Report`,
            t({ message: 'Fleet report', id: '[Report.Fleet]' })
          )
        );
    },
    [getFleetReport, handleSuccess, handleError]
  );

  const handleGetMaintenanceReport = useCallback(
    ({ id }: { id: number }) => {
      getMaintenanceReport({ id })
        .then(() =>
          handleSuccess(
            t`This export has been added to the export queue, you will be notified by email when the export is complete.`,
            t({ message: 'Maintenance report', id: '[Report.Maintenance]' })
          )
        )
        .catch(() =>
          handleError(
            t`There was a problem while creating Maintenance Report`,
            t({ message: 'Maintenance report', id: '[Report.Maintenance]' })
          )
        );
    },
    [getMaintenanceReport, handleSuccess, handleError]
  );

  const handleGetActuationsReport = useCallback(
    ({ accountId, userName, from, to }: { accountId: number; userName: string; from: Date; to: Date }) => {
      getActuationsCSV(
        {
          accountId: accountId?.toString(),
        },
        {
          additionalOptions: {
            params: {
              from: from?.toISOString() ?? '',
              to: to?.toISOString() ?? '',
            },
            responseType: 'blob',
          },
        }
      )
        .then((file) => {
          saveAs(file, `${t`Actuations`}_${userName ?? '-'}_${to ? format(to, 'dd-MM-yyyy') : ''}.csv`);
        })
        .catch(() =>
          handleError(
            t`There was a problem while creating Actuations CSV Report`,
            t({ message: 'Actuations CSV', id: '[Report.Actuations]' })
          )
        );
    },
    [getActuationsCSV, handleError]
  );

  return { handleGetFleetReport, handleGetActuationsReport, handleGetMaintenanceReport };
};
